import React from "react";
import styled from "styled-components";
import * as TypographyStyles from '../TypographyStyles';
import { colors } from "../../utils/colors";
import { ReactSVG } from 'react-svg';
import { LucideIcon } from 'lucide-react';
import { slateColors } from '../ContentPages/AppLandingPage/styles';

// Define styled components for the box and content
const Box = styled("div")<{ $bgColor: string; $isReliability: boolean }>`
  display: flex;
  width: 140px;
  height: 140px;
  padding: 1.25rem;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  border-radius: 1.75rem;
  flex-shrink: 0;
  border: ${props => props.$isReliability ? 'none' : `1px solid ${slateColors[200]}`};
  background: ${(props) => props.$bgColor};
  box-sizing: border-box;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1);
  transition: all 0.3s;
  
  &:hover {
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
    transform: translateY(-2px);
  }
`;

const IconWrapper = styled("div")<{ $iconColor: string }>`
  font-size: 24px;
  color: ${(props) => props.$iconColor};
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  
  svg {
    width: 24px;
    height: 24px;
    display: block;
  }
`;

const EmojiWrapper = styled("div")`
  font-size: 24px;
  line-height: 1;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: flex-start;
`;

const TopSection = styled("div")`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.25rem;
  width: 100%;
`;

const Content = styled("div")<{ $titleColor: string }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  color: ${(props) => props.$titleColor};
  gap: 0.25rem;
`;

const Title = styled("div")<{ $titleColor: string }>`
  color: ${(props) => props.$titleColor};
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  line-height: 1.2;
`;

const Value = styled("div")<{ $valueColor: string }>`
  color: ${(props) => props.$valueColor};
`;

type IconType = {
  type: 'svg';
  path: string;
} | {
  type: 'emoji';
  symbol: string;
} | {
  type: 'lucide';
  icon: LucideIcon;
};

// Updated props for the KPIBox
type KPIBoxProps = {
  icon: IconType;
  title: string;
  value: string | number;
  colorCondition: (value: number) => string;
  isReliability?: boolean;
};

const KPIBox: React.FC<KPIBoxProps> = ({
  icon,
  title,
  value,
  colorCondition,
  isReliability = false,
}) => {
  const numericValue = typeof value === 'number' ? value : parseFloat(value.toString());

  // Determine the base color based on the value
  const baseColor = isNaN(numericValue) ? colors.grey600 : colorCondition(numericValue);

  const darkVariant = {
    [colors.red]: colors.red,
    [colors.orange]: colors.orange,
    [colors.green]: colors.green,
    [colors.grey600]: colors.grey800,
  };

  // Determine colors based on whether the KPI is reliability or not
  const bgColor = isReliability ? baseColor : colors.white;
  const iconColor = isReliability ? colors.white : baseColor;
  const titleColor = isReliability ? colors.white : slateColors[900];
  const valueColor = isReliability ? colors.white : darkVariant[baseColor] || slateColors[900];

  const IconComponent = icon.type === 'lucide' ? icon.icon : null;

  return (
    <Box $bgColor={bgColor} $isReliability={isReliability}>
      <TopSection>
      {icon.type === 'svg' ? (
        <IconWrapper $iconColor={iconColor}>
          <ReactSVG
            src={icon.path}
            beforeInjection={(svg) => {
              svg.setAttribute('fill', iconColor);
            }}
          />
        </IconWrapper>
        ) : icon.type === 'lucide' && IconComponent ? (
          <IconWrapper $iconColor={iconColor}>
            <IconComponent size={24} />
          </IconWrapper>
        ) : icon.type === 'emoji' ? (
        <EmojiWrapper>
          {icon.symbol}
        </EmojiWrapper>
        ) : null}
      <Content $titleColor={titleColor}>
        <Title $titleColor={titleColor}>
          {title}
        </Title>
        </Content>
      </TopSection>
        <Value $valueColor={valueColor}>
          <TypographyStyles.KPIValueLarge>
            {value}
          </TypographyStyles.KPIValueLarge>
        </Value>
    </Box>
  );
};

export default KPIBox;